/**
 * Builds the field filter options of the relation audit log: the fields of the relation card sections
 * (the same fields as the field search on the relation card), plus fixed options for the items of the lists,
 * e.g. a communication channel, a link type, an authorization, a compliance item, a purpose or an assignment.
 */
// An Address line carries the medium (CloudX.Contracts.Enums.Medium) as field id
const addressLines = (...mediums) => ({
    typeFieldIds: mediums.map(medium => `Address:${medium}`),
});
// FieldType (CloudX.Model.Extra.FieldType) of the fields whose audit lines don't carry the field id,
// mapped to the audit lines (CloudX.Audit.Models.AuditLineType) they write, including their sub properties.
const AUDIT_LINES_BY_FIELD_TYPE = {
    9: addressLines(0, 5),
    12: { types: ['Address'] },
    13: { types: ['AddressSetting'] },
    25: addressLines(1),
    26: addressLines(2),
    27: addressLines(3),
    39: addressLines(4),
    14: { types: ['RelationLink', 'RelationLinkProperty'] },
    33: { types: ['RelationLink', 'RelationLinkProperty'] },
    29: { types: ['NaceCode', 'NaceCodeProperty'] },
    45: { types: ['Compliance'] },
    46: { types: ['Authorization', 'AuthorizationProperty'] },
    55: { types: ['InvoiceStream', 'InvoiceStreamProperty'] },
    58: { types: ['PlannedRegistration', 'PlannedRegistrationProperty'] },
    68: { types: ['UboSnapshot', 'UboSnapshotEnteredOnline'] },
    76: { types: ['BankAccount', 'BankAccountProperty'] }, // BankAccounts
};
// RelationFields, used as path of the list items
const ADDRESSES_FIELD_ID = '5cb65c0b-308b-48a8-9038-9b2f9c068869';
const COMMUNICATION_FIELD_ID = '004c6123-51b9-43ba-8e80-27af0328d285';
const LINKS_FIELD_ID = '2ca70dce-5a5d-49c1-a9d6-776c97e14fff';
const AUTHORIZATIONS_FIELD_ID = '3d6de6a8-b4f2-428a-a8f5-c5ee2467c913';
const INVOICE_STREAM_FIELD_ID = '00ac639e-027a-4589-90fa-9017873c8e5a';
// The main communication channels (CloudX.Contracts.Enums.Medium), the first medium is used for the label
const CHANNEL_MEDIUMS = [
    [0, 5],
    [1],
    [2],
    [4], // Mobile
];
// CloudX.Contracts.Enums.Purpose of the communication channels
const PURPOSES = [0, 1, 2, 3, 4];
// CloudX.Model.Master.Enums.ComplianceType mapped to its property in CloudX.Contracts.ServiceBus.ComplianceChangedEventModel
const COMPLIANCE_PROPERTY_BY_TYPE = {
    0: 'AssignmentLetterStatus',
    1: 'RiskAssessmentStatus',
    2: 'BeneficiariesStatus',
    3: 'IdCardStatus',
    4: 'AuthorizationsStatus',
    5: 'UBOStatus',
    6: 'IdentificationStatus',
    7: 'AuthorizationletterStatus',
    8: 'DetailedRiskAssessmentStatus',
    9: 'OriginOfFundsStatus',
    10: 'KvkExtractStatus',
};
// The invoice stream without assignment (see RelationAuditFilter.WithoutAssignment)
const WITHOUT_ASSIGNMENT = 'none';
const getListItemOptions = ({ linkTypes, authorizationNames, complianceTypes, compliancePath, invoiceStreams, getInvoiceStreamName, }, translate) => {
    const path = (fieldId, fallback) => translate('field', fieldId, fallback);
    const channels = CHANNEL_MEDIUMS.map(mediums => ({
        id: `medium-${mediums[0]}`,
        label: translate('medium', String(mediums[0])),
        path: path(ADDRESSES_FIELD_ID, 'Addresses'),
        selection: addressLines(...mediums),
    }));
    // An AddressSetting line carries the purpose as field id
    const purposes = PURPOSES.map(purpose => ({
        id: `purpose-${purpose}`,
        label: translate('purpose', String(purpose)),
        path: path(COMMUNICATION_FIELD_ID, 'Communication'),
        selection: { typeFieldIds: [`AddressSetting:${purpose}`] },
    }));
    // Link property changes only carry the link type since it was added to the audit
    const links = (linkTypes !== null && linkTypes !== void 0 ? linkTypes : []).map(linkType => ({
        id: `linktype-${linkType.key}`,
        label: translate('linktypeto', String(linkType.key), linkType.value),
        path: path(LINKS_FIELD_ID, 'Links'),
        selection: { linkTypes: [linkType.key] },
    }));
    const authorizations = (authorizationNames !== null && authorizationNames !== void 0 ? authorizationNames : []).map(name => ({
        id: `authorization-${name.key}`,
        label: translate('authorizationname', String(name.key), name.value),
        path: path(AUTHORIZATIONS_FIELD_ID, 'Authorizations'),
        selection: { authorizationNames: [name.key] },
    }));
    // A compliance line has no field id, its item is the property
    const compliance = (complianceTypes !== null && complianceTypes !== void 0 ? complianceTypes : [])
        .filter(complianceType => COMPLIANCE_PROPERTY_BY_TYPE[complianceType.key])
        .map(complianceType => ({
        id: `compliance-${complianceType.key}`,
        label: translate('compliancetype', String(complianceType.key), complianceType.value),
        path: compliancePath,
        selection: {
            typeProperties: [`Compliance:${COMPLIANCE_PROPERTY_BY_TYPE[complianceType.key]}`],
        },
    }));
    // Only useful when the relation has invoice streams for assignments
    const assignments = (invoiceStreams !== null && invoiceStreams !== void 0 ? invoiceStreams : []).some(x => x.assignmentId)
        ? invoiceStreams.map(invoiceStream => {
            var _a, _b;
            return ({
                id: `assignment-${(_a = invoiceStream.assignmentId) !== null && _a !== void 0 ? _a : WITHOUT_ASSIGNMENT}`,
                label: getInvoiceStreamName(invoiceStream),
                path: path(INVOICE_STREAM_FIELD_ID, 'Invoice stream'),
                selection: { assignmentIds: [(_b = invoiceStream.assignmentId) !== null && _b !== void 0 ? _b : WITHOUT_ASSIGNMENT] },
            });
        })
        : [];
    return [...channels, ...purposes, ...links, ...authorizations, ...compliance, ...assignments];
};
const toOption = (field, path, translate) => ({
    id: field.id,
    label: translate('field', field.id, field.label),
    path,
    selection: Object.assign({ fieldIds: [field.id] }, AUDIT_LINES_BY_FIELD_TYPE[field.type]),
});
const extractFields = (section, parentPath, translate) => {
    var _a, _b, _c;
    const pathParts = [...parentPath, translate('section', section.id, section.label)];
    const path = pathParts.join(' › ');
    return [
        ...((_a = section.lists) !== null && _a !== void 0 ? _a : []).map(x => toOption(x, path, translate)),
        ...((_b = section.columns) !== null && _b !== void 0 ? _b : []).flatMap(column => column.map(x => toOption(x, path, translate))),
        ...((_c = section.sections) !== null && _c !== void 0 ? _c : []).flatMap(x => extractFields(x, pathParts, translate)),
    ];
};
/** The list items and all fields of the sections, one option per id, sorted on label */
export const getAuditFieldOptions = (sections, lookups, translate) => {
    const options = [
        ...getListItemOptions(lookups, translate),
        ...(sections !== null && sections !== void 0 ? sections : []).flatMap(x => extractFields(x, [], translate)),
    ];
    const unique = options.filter((x, index) => options.findIndex(o => o.id === x.id) === index);
    return unique.sort((a, b) => a.label.localeCompare(b.label));
};
/** Query string parameters of the audit API for the selected options, comma separated */
export const getAuditFieldFilter = (selected) => {
    const values = {};
    selected.forEach(({ selection }) => Object.entries(selection).forEach(([parameter, parameterValues]) => {
        var _a;
        values[parameter] = (_a = values[parameter]) !== null && _a !== void 0 ? _a : new Set();
        parameterValues.forEach(value => values[parameter].add(value));
    }));
    return Object.entries(values).map(([parameter, parameterValues]) => [
        parameter,
        [...parameterValues].join(','),
    ]);
};
